import React, { useMemo, useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import { Row, Col, Badge, Button, Form } from "react-bootstrap";
import ClickBack from "../../components/buttons/ClickBack";
import DynamicTable from "../../components/DynamicTable";
import { fetchLoginAttemptsData } from "./dataService";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import SearchBar from "../../components/SearchBar";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("fr", fr);

function ListScreen(props) {
  const [attemptsData, setAttemptsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [nbr, setNbr] = useState(1);
  const [dateDebut, setDateDebut] = useState(null);
  const [dateFin, setDateFin] = useState(null);
  const [successFilter, setSuccessFilter] = useState("");
  const [ipAddress, setIpAddress] = useState("");
  const [failureReason, setFailureReason] = useState("");
  const [refererFilter, setRefererFilter] = useState("");

  // Function to fetch login attempts data
  const fetchData = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (dateDebut) filters.date_from = format(dateDebut, "yyyy-MM-dd");
      if (dateFin) filters.date_to = format(dateFin, "yyyy-MM-dd");
      if (successFilter !== "") filters.success = successFilter;
      if (ipAddress) filters.ip_address = ipAddress;
      if (failureReason) filters.failure_reason = failureReason;
      if (refererFilter) filters.referer = refererFilter;

      const data = await fetchLoginAttemptsData(nbr, searchTerm, filters);
      setAttemptsData(data.data);
    } catch (error) {
      console.error("Error fetching login attempts data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Update search term and fetch data
  const handleSearch = (term) => {
    setSearchTerm(term);
    setNbr(1); // Reset to first page
  };

  // useEffect to automatically load data when the component mounts
  useEffect(() => {
    fetchData();
  }, [nbr, searchTerm, dateDebut, dateFin, successFilter, ipAddress, failureReason, refererFilter]);

  // Memoized columns for the DynamicTable component
  const columns = useMemo(() => {
    return [
      { Header: "ID", accessor: "id" },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Utilisateur",
        accessor: "user",
        Cell: ({ row }) => {
          const user = row.original.user;
          return user ? `${user.prenom || ''} ${user.nom || ''}` : 'N/A';
        }
      },
      {
        Header: "Statut",
        accessor: "success",
        Cell: ({ value }) => {
          return value ? (
            <Badge bg="success">Réussi</Badge>
          ) : (
            <Badge bg="danger">Échoué</Badge>
          );
        }
      },
      {
        Header: "Raison de l'échec",
        accessor: "failure_reason",
        Cell: ({ value }) => {
          const reasonLabels = {
            'invalid_credentials': 'Identifiants invalides',
            'account_locked': 'Compte verrouillé',
            'user_not_found': 'Utilisateur introuvable',
            'account_inactive': 'Compte inactif',
            // Connexion via appareil de confiance
            'device_token_unknown': 'Appareil inconnu',
            'device_token_expired': 'Appareil expiré',
            'device_account_disabled': 'Compte désactivé (appareil)',
            'device_email_not_found': 'Email introuvable (appareil)',
            // Tunnel de réinitialisation de mot de passe
            'password_reset_requested': 'Code de réinitialisation envoyé',
            'password_reset_unknown_email': 'Réinitialisation — email inconnu',
            'password_reset_code_verified': 'Code de réinitialisation vérifié',
            'password_reset_code_invalid': 'Code de réinitialisation invalide',
            'password_reset_completed': 'Mot de passe réinitialisé',
          };
          return value ? (
            <Badge bg="warning">{reasonLabels[value] || value}</Badge>
          ) : (
            'N/A'
          );
        }
      },
      {
        Header: "IP",
        accessor: "ip_address",
      },
      {
        Header: "Source",
        accessor: "source",
        Cell: ({ value, row }) => {
          const sourceIcons = {
            'Web Frontend': '🌐',
            'Web BackOffice': '⚙️',
            'Web Dev': '💻',
            'Web marion livraison': '🚚',
            'Mobile iOS App': '📱',
            'Mobile Android App': '🤖',
            'Mobile iOS Browser': '🍎',
            'Mobile Android Browser': '🟢',
            'Postman': '📮',
            'cURL': '⌨️',
            'Python Script': '🐍',
            'Unknown': '❓'
          };
          const icon = sourceIcons[value] || '❓';
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>{icon}</span>
              <span>{value || 'Unknown'}</span>
            </div>
          );
        }
      },
      {
        Header: "Navigateur",
        accessor: "browser",
        Cell: ({ value }) => value || 'N/A'
      },
      {
        Header: "Referer",
        accessor: "referer",
        Cell: ({ value }) => (
          <div style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={value || 'N/A'}>
            {value ? (
              <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#0d6efd', textDecoration: 'none' }}>
                {value}
              </a>
            ) : (
              'N/A'
            )}
          </div>
        )
      },
      {
        Header: "User Agent",
        accessor: "user_agent",
        Cell: ({ value }) => (
          <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={value || 'N/A'}>
            {value || 'N/A'}
          </div>
        )
      },
      {
        Header: "Date",
        accessor: "attempted_at",
        Cell: ({ value }) =>
          format(new Date(value), "dd-MM-yyyy HH:mm:ss", { locale: fr }),
      },
    ].filter(Boolean);
  }, [nbr]);

  // Render loading message while data is being fetched
  if (!attemptsData) {
    return (
      <Container>
        <p>Chargement...</p>
      </Container>
    );
  }

  // Render the component
  return (
    <Container>
      <div className="loginAttemptsList">
        {/* Breadcrumb navigation */}
        <p className="h6">Accueil / Administration / Tentatives de connexion</p>

        {/* Header */}
        <Row className="pt-4 pb-2">
          <Col style={{ display: "flex", alignItems: "center" }}>
            <ClickBack />
            <h1 className="p-1">Tentatives de connexion</h1>
          </Col>
        </Row>

        <SearchBar
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          placeholder="Rechercher par email..."
        />

        {/* Filtres */}
        <Row className="mb-3 align-items-end">
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Date de début</Form.Label>
              <DatePicker
                selected={dateDebut}
                onChange={(date) => {
                  setDateDebut(date);
                  setNbr(1);
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="Date de début"
                isClearable
                className="form-control"
                locale="fr"
              />
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Date de fin</Form.Label>
              <DatePicker
                selected={dateFin}
                onChange={(date) => {
                  setDateFin(date);
                  setNbr(1);
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="Date de fin"
                isClearable
                className="form-control"
                locale="fr"
              />
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Statut</Form.Label>
              <Form.Select
                value={successFilter}
                onChange={(e) => {
                  setSuccessFilter(e.target.value);
                  setNbr(1);
                }}
              >
                <option value="">Tous</option>
                <option value="1">Réussi</option>
                <option value="0">Échoué</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Raison de l'échec</Form.Label>
              <Form.Select
                value={failureReason}
                onChange={(e) => {
                  setFailureReason(e.target.value);
                  setNbr(1);
                }}
              >
                <option value="">Toutes les raisons</option>
                <optgroup label="Connexion">
                  <option value="invalid_credentials">Identifiants invalides</option>
                  <option value="account_locked">Compte verrouillé</option>
                  <option value="user_not_found">Utilisateur introuvable</option>
                  <option value="account_inactive">Compte inactif</option>
                </optgroup>
                <optgroup label="Appareil de confiance">
                  <option value="device_token_unknown">Appareil inconnu</option>
                  <option value="device_token_expired">Appareil expiré</option>
                  <option value="device_account_disabled">Compte désactivé (appareil)</option>
                  <option value="device_email_not_found">Email introuvable (appareil)</option>
                </optgroup>
                <optgroup label="Mot de passe oublié">
                  <option value="password_reset_requested">Code de réinitialisation envoyé</option>
                  <option value="password_reset_unknown_email">Réinitialisation — email inconnu</option>
                  <option value="password_reset_code_verified">Code de réinitialisation vérifié</option>
                  <option value="password_reset_code_invalid">Code de réinitialisation invalide</option>
                  <option value="password_reset_completed">Mot de passe réinitialisé</option>
                </optgroup>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Adresse IP</Form.Label>
              <Form.Control
                type="text"
                placeholder="Filtrer par IP..."
                value={ipAddress}
                onChange={(e) => {
                  setIpAddress(e.target.value);
                  setNbr(1);
                }}
              />
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Source/Referer</Form.Label>
              <Form.Control
                type="text"
                placeholder="Frontend, Mobile, etc..."
                value={refererFilter}
                onChange={(e) => {
                  setRefererFilter(e.target.value);
                  setNbr(1);
                }}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Render the DynamicTable component */}
        {loading ? (
          <p className="lead">Chargement...</p>
        ) : (
          <DynamicTable
            columns={columns}
            data={attemptsData?.data}
            link={attemptsData?.links}
            last_page={attemptsData?.last_page}
            setNbr={setNbr}
            nbr={nbr}
            total={attemptsData?.total}
            name="loginAttempt"
          />
        )}
      </div>
    </Container>
  );
}

export default ListScreen;
